import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getReceivables, getOpeningBalances } from '../../api';
import { outstandingOf } from '../Invoices';
import { formatRupeesUI, formatDateDdMmmYy } from '../../utils/format';

/**
 * Receivables on a phone: who owes the firm money and how late it is.
 *
 * The desktop ReceivablesTab is a sortable pivot with eight numeric columns,
 * export, write-offs, reminder emails and opening-balance administration.
 * None of that survives 375px. What does survive is the collections question
 * a partner asks away from a desk — which client, how much, how old — so this
 * is the client pivot, biggest debt first, drilling into the invoices behind
 * a balance.
 *
 * The money arithmetic is the desktop's, not a phone-sized approximation:
 * outstandingOf() for invoices, the same opening-balance netting, the same
 * ageing buckets, the same >0.5 settled cut-off. Two surfaces disagreeing
 * about what a client owes would be worse than having no phone view at all.
 */

type RecvRow = {
  key: string;
  source: 'invoice' | 'opening';
  id: number;
  invoiceNumber: string;
  clientCode: string;
  clientName: string;
  clientId: number | null;
  outstanding: number;
  invoiceDate: string;
  dueDate: string | null;
  bucket: string;
  daysOverdue: number;
};

type ClientRow = {
  key: string;
  clientCode: string;
  clientName: string;
  count: number;
  outstanding: number;
  b0to30: number;
  b31to60: number;
  b61to90: number;
  b90plus: number;
  maxDays: number;
  rows: RecvRow[];
};

// Ageing for opening balances, computed client-side to mirror the invoice
// backend (invoice rows arrive with ageingBucket/daysOverdue already set).
// Copied from ReceivablesTab — the bucket edges must match exactly or the two
// screens would age the same debt differently.
const ageingOf = (dueDate: string | null, invoiceDate: string): { bucket: string; daysOverdue: number } => {
  const ref = dueDate ? new Date(dueDate) : new Date(new Date(invoiceDate).getTime() + 30 * 86400000);
  ref.setHours(0, 0, 0, 0);
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  const days = Math.floor((t.getTime() - ref.getTime()) / 86400000);
  const bucket = days <= 0 ? 'Current' : days <= 30 ? '0-30 days' : days <= 60 ? '31-60 days' : days <= 90 ? '61-90 days' : '90+ days';
  return { bucket, daysOverdue: Math.max(0, days) };
};

const MobileReceivables: React.FC<{ onOpenInvoice: (invoiceId: number) => void | Promise<void> }> = ({ onOpenInvoice }) => {
  const [items, setItems] = useState<any[]>([]);
  const [openingBalances, setOpeningBalances] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [opening, setOpening] = useState<number | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      getReceivables().catch(() => ({ data: [] })),
      getOpeningBalances().catch(() => ({ data: [] })),
    ])
      .then(([recRes, obRes]) => {
        const raw: any = recRes.data;
        setItems(Array.isArray(raw) ? raw : (raw?.invoices ?? []));
        setOpeningBalances(obRes.data || []);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo<RecvRow[]>(() => {
    const invRows: RecvRow[] = items.map((r: any) => ({
      key: `inv-${r.id}`,
      source: 'invoice',
      id: r.id,
      invoiceNumber: r.invoiceNumber,
      clientCode: r.client?.clientCode ?? '',
      clientName: r.client?.clientName ?? '—',
      clientId: r.client?.id ?? r.clientId ?? null,
      outstanding: outstandingOf(r),
      invoiceDate: r.invoiceDate,
      dueDate: r.dueDate ?? null,
      bucket: r.ageingBucket ?? '',
      daysOverdue: r.daysOverdue ?? 0,
    }));
    const obRows: RecvRow[] = openingBalances
      .filter((ob: any) => ob.status !== 'PAID' && ob.status !== 'WRITTEN_OFF')
      .map((ob: any) => {
        const a = ageingOf(ob.dueDate ?? null, ob.invoiceDate);
        return {
          key: `ob-${ob.id}`,
          source: 'opening' as const,
          id: ob.id,
          invoiceNumber: ob.invoiceNumber,
          clientCode: ob.client?.clientCode ?? '',
          clientName: ob.client?.clientName ?? '—',
          clientId: ob.clientId ?? ob.client?.id ?? null,
          // Credit notes and write-offs reduce an opening balance exactly as a
          // payment does; invoice rows get this netting from outstandingOf.
          outstanding: Math.max(0, Number(ob.amount) - Number(ob.amountPaid) - Number(ob.creditedAmount ?? 0) - Number(ob.writtenOffAmount ?? 0)),
          invoiceDate: ob.invoiceDate,
          dueDate: ob.dueDate ?? null,
          bucket: a.bucket,
          daysOverdue: a.daysOverdue,
        };
      });
    // A zero (or sub-rupee) balance is settled, not a receivable.
    return [...invRows, ...obRows].filter((r) => r.outstanding > 0.5);
  }, [items, openingBalances]);

  const totals = useMemo(() => {
    let grand = 0;
    let overdue = 0;
    for (const r of rows) {
      grand += r.outstanding;
      if (r.daysOverdue > 0) overdue += r.outstanding;
    }
    return { grand, overdue };
  }, [rows]);

  const clients = useMemo<ClientRow[]>(() => {
    const q = search.trim().toLowerCase();
    const map = new Map<string, ClientRow>();
    for (const r of rows) {
      if (q && !r.clientName.toLowerCase().includes(q) && !r.clientCode.toLowerCase().includes(q) && !r.invoiceNumber.toLowerCase().includes(q)) {
        continue;
      }
      const key = r.clientId != null ? String(r.clientId) : `name:${r.clientName}`;
      let g = map.get(key);
      if (!g) {
        g = {
          key, clientCode: r.clientCode, clientName: r.clientName,
          count: 0, outstanding: 0, b0to30: 0, b31to60: 0, b61to90: 0, b90plus: 0, maxDays: 0, rows: [],
        };
        map.set(key, g);
      }
      g.count++;
      g.outstanding += r.outstanding;
      g.maxDays = Math.max(g.maxDays, r.daysOverdue);
      g.rows.push(r);
      if (r.bucket === '31-60 days') g.b31to60 += r.outstanding;
      else if (r.bucket === '61-90 days') g.b61to90 += r.outstanding;
      else if (r.bucket === '90+ days') g.b90plus += r.outstanding;
      else g.b0to30 += r.outstanding; // Current + 0-30 days, as on the desktop
    }
    const arr = Array.from(map.values());
    for (const g of arr) g.rows.sort((a, b) => b.outstanding - a.outstanding);
    // Biggest debt first: on a phone the top of the list is all most people
    // will read, so it has to be the money that matters.
    arr.sort((a, b) => b.outstanding - a.outstanding);
    return arr;
  }, [rows, search]);

  const openInvoice = async (row: RecvRow) => {
    if (row.source !== 'invoice') return;
    setOpening(row.id);
    try {
      await onOpenInvoice(row.id);
    } finally {
      setOpening(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="p-3 bg-white border border-gray-200 rounded-xl">
          <p className="text-xs text-gray-500">Outstanding</p>
          <p className="text-lg font-bold text-gray-900 mt-0.5 truncate">{formatRupeesUI(totals.grand)}</p>
        </div>
        <div className="p-3 bg-white border border-gray-200 rounded-xl">
          <p className="text-xs text-gray-500">Overdue</p>
          <p className={`text-lg font-bold mt-0.5 truncate ${totals.overdue > 0 ? 'text-red-600' : 'text-gray-900'}`}>
            {formatRupeesUI(totals.overdue)}
          </p>
        </div>
      </div>

      <input
        type="search"
        className="input-field py-2.5"
        placeholder="Search client or invoice no."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <div className="space-y-2">
        {loading ? (
          <p className="py-10 text-center text-sm text-gray-400">Loading…</p>
        ) : clients.length === 0 ? (
          <p className="py-10 text-center text-sm text-gray-400">
            {rows.length === 0 ? 'Nothing outstanding' : 'No matching client'}
          </p>
        ) : (
          clients.map((c) => {
            const isOpen = expanded === c.key;
            return (
              <div key={c.key} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setExpanded(isOpen ? null : c.key)}
                  aria-expanded={isOpen}
                  className="w-full text-left p-4 active:bg-gray-50"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{c.clientName}</p>
                      <p className="text-xs text-gray-500 truncate mt-0.5">
                        {c.clientCode ? `${c.clientCode} · ` : ''}
                        {c.count} invoice{c.count === 1 ? '' : 's'}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold text-gray-900">{formatRupeesUI(c.outstanding)}</p>
                      <p className={`text-[11px] mt-0.5 ${c.maxDays > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                        {c.maxDays > 0 ? `${c.maxDays}d overdue` : 'Not yet due'}
                      </p>
                    </div>
                  </div>

                  {/* Only non-empty buckets: four fixed columns would spend a
                      whole row of a 375px card on zeroes. */}
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    <Bucket label="0-30" amount={c.b0to30} tone="text-gray-600 bg-gray-100" />
                    <Bucket label="31-60" amount={c.b31to60} tone="text-amber-700 bg-amber-50" />
                    <Bucket label="61-90" amount={c.b61to90} tone="text-orange-700 bg-orange-50" />
                    <Bucket label="90+" amount={c.b90plus} tone="text-red-700 bg-red-50" />
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-gray-100 divide-y divide-gray-100 bg-gray-50/60">
                    {c.rows.map((r) => {
                      const body = (
                        <>
                          <div className="flex items-baseline justify-between gap-3">
                            <p className="text-sm font-medium text-gray-900 truncate">{r.invoiceNumber}</p>
                            <span className="text-sm font-semibold text-gray-900 shrink-0">
                              {formatRupeesUI(r.outstanding)}
                            </span>
                          </div>
                          <div className="flex items-baseline justify-between gap-3 mt-0.5">
                            <p className="text-xs text-gray-500 truncate">
                              {r.source === 'opening' ? 'Opening balance · ' : ''}
                              {formatDateDdMmmYy(r.invoiceDate)}
                              {r.dueDate ? ` · due ${formatDateDdMmmYy(r.dueDate)}` : ''}
                            </p>
                            <span className={`text-[11px] shrink-0 ${r.daysOverdue > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                              {r.daysOverdue > 0 ? `${r.daysOverdue}d` : 'Current'}
                            </span>
                          </div>
                        </>
                      );
                      // Opening balances predate invoicing in this system and
                      // have no invoice record to open, so they stay inert.
                      return r.source === 'invoice' ? (
                        <button
                          key={r.key}
                          onClick={() => openInvoice(r)}
                          disabled={opening === r.id}
                          className="w-full text-left px-4 py-3 min-h-[44px] active:bg-gray-100 disabled:opacity-60"
                        >
                          {body}
                        </button>
                      ) : (
                        <div key={r.key} className="px-4 py-3 min-h-[44px]">
                          {body}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const Bucket: React.FC<{ label: string; amount: number; tone: string }> = ({ label, amount, tone }) =>
  amount > 0.5 ? (
    <span className={`px-2 py-1 rounded-md text-[11px] font-medium ${tone}`}>
      {label}: {formatRupeesUI(amount)}
    </span>
  ) : null;

export default MobileReceivables;
